import { useEffect, useState } from 'react'
import useSWR from 'swr'

import { QUERY_KEYS } from '@forwardflow/helpers/utils/constants'
import OrganizationService from '@forwardflow/services/OrganizationService'

interface UseOrganizationUsersInfiniteParams {
   invitationStatus: 'accepted' | 'pending'
   limit?: number
}

export const useOrganizationUsersInfinite = ({
   invitationStatus,
   limit = 10,
}: UseOrganizationUsersInfiniteParams) => {
   const [page, setPage] = useState(1)
   const [users, setUsers] = useState<any[]>([])
   const [hasMore, setHasMore] = useState(true)
   const [resetKey, setResetKey] = useState(0)
   const [totalItems, setTotalItems] = useState(0)

   const { data, isValidating } = useSWR(
      [QUERY_KEYS.ORGANIZATION_USERS, resetKey, { invitationStatus, page, limit }],
      () =>
         OrganizationService.getOrganizationUsersLists({
            invitationStatus,
            page,
            limit,
         }),
      { shouldRetryOnError: false }
   )

   useEffect(() => {
      if (!data) return

      const incoming = data.data ?? []
      const currentPage = data.meta?.currentPage ?? page
      const totalPages = data.meta?.totalPages ?? 1
      setTotalItems(data.meta?.totalItems ?? 0)
      setHasMore(currentPage < totalPages)

      setUsers((prev) => {
         if (page === 1) return incoming
         const existingEmails = new Set(prev.map((u) => u.email))
         const deduped = incoming.filter((u) => !existingEmails.has(u.email))
         return [...prev, ...deduped]
      })
   }, [data])

   const loadMore = () => {
      if (isValidating || !hasMore) return
      setPage((p) => p + 1)
   }

   const refetch = () => {
      setPage(1)
      setUsers([])
      setHasMore(true)
      setResetKey((k) => k + 1)
   }

   return {
      users,
      isLoading: isValidating,
      hasMore,
      totalItems,
      loadMore,
      refetch,
   }
}
